import { useState, useEffect } from 'react';
import { ref, onValue, off } from 'firebase/database';
import { rtdb } from '@/lib/firebase';

export interface UserPresence {
  isOnline: boolean;
  lastSeen: number | null;
  status?: 'online' | 'away' | 'offline';
}

export function useUserPresence(userId: string | undefined): UserPresence {
  const [presence, setPresence] = useState<UserPresence>({
    isOnline: false,
    lastSeen: null,
    status: 'offline'
  });

  useEffect(() => {
    if (!userId) {
      setPresence({
        isOnline: false,
        lastSeen: null,
        status: 'offline'
      });
      return;
    }

    const userStatusRef = ref(rtdb, `status/${userId}`);

    const unsubscribe = onValue(userStatusRef, (snapshot) => {
      const data = snapshot.val();
      
      if (data) {
        const now = Date.now();
        const lastSeenTimestamp = data.lastSeen || now;
        const isOnline = data.isOnline === true;
        
        // Determine status based on last seen time
        let status: 'online' | 'away' | 'offline' = 'offline';
        
        if (isOnline) {
          status = 'online';
        } else if (lastSeenTimestamp) {
          const timeDiff = now - lastSeenTimestamp;
          const minutesAgo = timeDiff / (1000 * 60);
          
          if (minutesAgo < 5) {
            status = 'away'; // Recently active
          } else {
            status = 'offline';
          }
        }
        
        setPresence({
          isOnline,
          lastSeen: lastSeenTimestamp,
          status
        });
      } else {
        setPresence({
          isOnline: false,
          lastSeen: null,
          status: 'offline'
        });
      }
    }, (error) => {
      console.error('Error listening to user presence:', error);
      setPresence({
        isOnline: false,
        lastSeen: null,
        status: 'offline'
      });
    });

    return () => {
      off(userStatusRef);
    };
  }, [userId]);

  return presence;
}

export function useMultipleUserPresence(userIds: string[]): Record<string, UserPresence> {
  const [presences, setPresences] = useState<Record<string, UserPresence>>({});

  useEffect(() => {
    if (!userIds || userIds.length === 0) {
      setPresences({});
      return;
    }

    const unsubscribes: (() => void)[] = [];

    userIds.forEach((userId) => {
      const userStatusRef = ref(rtdb, `status/${userId}`);

      const unsubscribe = onValue(userStatusRef, (snapshot) => {
        const data = snapshot.val();
        
        setPresences((prev) => {
          if (data) {
            const now = Date.now();
            const lastSeenTimestamp = data.lastSeen || now;
            const isOnline = data.isOnline === true;
            
            let status: 'online' | 'away' | 'offline' = 'offline';
            
            if (isOnline) {
              status = 'online';
            } else if (lastSeenTimestamp) {
              const timeDiff = now - lastSeenTimestamp;
              const minutesAgo = timeDiff / (1000 * 60);
              
              if (minutesAgo < 5) {
                status = 'away';
              } else {
                status = 'offline';
              }
            }
            
            return {
              ...prev,
              [userId]: {
                isOnline,
                lastSeen: lastSeenTimestamp,
                status
              }
            };
          } else {
            return {
              ...prev,
              [userId]: {
                isOnline: false,
                lastSeen: null,
                status: 'offline'
              }
            };
          }
        });
      });

      unsubscribes.push(() => off(userStatusRef));
    });

    return () => {
      unsubscribes.forEach((unsub) => unsub());
    };
  }, [userIds.join(',')]);

  return presences;
}
